import _ from 'lodash';
import { permissionsStructure } from './constants';

type CamelizeString<T extends PropertyKey, C extends string = ''> = T extends string
	? string extends T
		? string
		: T extends `${infer F}_${infer R}`
			? CamelizeString<Capitalize<R>, `${C}${F}`>
			: `${C}${T}`
	: T;

type PermissionsStructure = typeof permissionsStructure;

type Category = PermissionsStructure[number]['categories'][number];
export type PermissionValue =
	| Category['permissions'][number]['value']
	| PermissionsStructure[number]['permissions'][number]['value']
	| '*'
	// Internal, non-assignable principal permission granted to authenticated devices
	// (kiosks). Not part of `permissionsStructure`, so it is never shown/assignable in
	// the roles UI. Mirrors the special-cased `siteAdmin` ('*') below.
	| 'device';

export type PermissionLabel =
	| Category['permissions'][number]['label']
	| PermissionsStructure[number]['permissions'][number]['label'];

export const permissionsList = permissionsStructure
	.flatMap(({ categories = [] }) => categories as Category[])
	.flatMap((category) => {
		return category.permissions.map(({ value, label }) => ({
			value: value as PermissionValue,
			label: label as PermissionLabel,
		}));
	})

	.concat(
		permissionsStructure.flatMap(({ permissions = [] }) =>
			permissions.map(({ value, label }) => ({ value: value as PermissionValue, label: label as PermissionLabel })),
		),
	);
type PermissionKey = CamelizeString<PermissionValue> | 'siteAdmin' | 'device';

export const PERMISSIONS = permissionsList.reduce(
	(acc, permission) => {
		const key = _.camelCase(permission.value);
		acc[key] = permission.value;
		return acc;
	},
	// `siteAdmin` ('*') and `device` are special, non-assignable principal permissions
	// that are not part of `permissionsStructure` (so they never appear in the roles UI).
	{ siteAdmin: '*', device: 'device' },
) as Record<PermissionKey, PermissionValue>;

export const PERMISSION_LABELS = permissionsList.reduce(
	(acc, permission) => {
		acc[permission.value] = permission.label;
		return acc;
	},
	{} as Record<PermissionValue, PermissionLabel>,
);
